import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "../components/Auth/AuthSlice";
import { profileSelector } from "../components/Profile/ProfileSlice";
import {
  canAccessAbac,
  canAccessAbacAny,
  canAccessAnySmsTemplateAction,
  canAccessDefaultSetting,
  canAccessNavbarItem,
  canAccessNavbarPermission,
  canAccessPlatformDocApiCard,
  canAccessPlatformDocFeature,
  canAccessFeatureDocument,
  canViewPlatformDocumentsUi,
  canViewSdkDocumentsUi,
  canViewSmscProvidersUi,
  canViewFirewallUi,
  canViewSmsDashboard,
  canAccessSmsTemplate,
  canAccessWalletBalance,
  canAccessWalletDownload,
  canAccessWalletPage,
  canAccessWalletPricing,
  canAccessWalletPricingDownload,
  isMultiAccessUser,
  shouldEnforceAbacForUser,
} from "../components/User/Abac/abacAccess";

/**
 * True while a multi-access member has no permissions yet (first load after login).
 * Without permissions every check allows, so UI must wait instead of rendering.
 */
export function useAbacSessionPending() {
  const { user, abacPermissions, abacSessionLoaded } =
    useSelector(authSelector);
  return (
    abacPermissions == null &&
    !abacSessionLoaded &&
    shouldEnforceAbacForUser(user, globalThis.profileData)
  );
}

/**
 * Session ABAC helpers for multi-access UI gating (sidebar, navbar, pages).
 * Non multi-access users always get allow=true.
 * Initial permissions come from GET /multiUser/permissions once per session;
 * later changes arrive via the /user/queue/abac-permissions websocket.
 */
export default function useAbacAccess() {
  const { user, abacPermissions, abacSessionLoaded, isFetchingAbacSession } =
    useSelector(authSelector);
  const profileState = useSelector(profileSelector) || {};
  const profileData = profileState?.getProfile || globalThis.profileData || {};

  const isMultiAccess = useMemo(
    () => isMultiAccessUser(user, profileData),
    [user, profileData]
  );
  const shouldEnforce = useMemo(
    () => shouldEnforceAbacForUser(user, profileData),
    [user, profileData]
  );

  const canAccess = useCallback(
    (...pathOrSegments) => {
      const path =
        pathOrSegments.length === 1 && Array.isArray(pathOrSegments[0])
          ? pathOrSegments[0]
          : pathOrSegments;
      return canAccessAbac(abacPermissions, path, user, profileData);
    },
    [abacPermissions, user, profileData]
  );

  const canAccessAny = useCallback(
    (paths) => canAccessAbacAny(abacPermissions, paths, user, profileData),
    [abacPermissions, user, profileData]
  );

  return {
    user,
    profileData,
    abacPermissions,
    abacSessionLoaded: Boolean(abacSessionLoaded),
    isFetchingAbacSession: Boolean(isFetchingAbacSession),
    isMultiAccess,
    shouldEnforce,
    canAccess,
    canAccessAny,
    // SMS templates (sms.templates.<action>)
    canAccessTemplate: (action = "view") =>
      canAccessSmsTemplate(abacPermissions, action, user, profileData),
    canAccessAnyTemplateAction: () =>
      canAccessAnySmsTemplateAction(abacPermissions, user, profileData),
    canAccessWalletUi: canAccessWalletBalance(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletPage: canAccessWalletPage(abacPermissions, user, profileData),
    canAccessWalletDownload: canAccessWalletDownload(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletPricing: canAccessWalletPricing(
      abacPermissions,
      user,
      profileData
    ),
    canAccessWalletPricingDownload: canAccessWalletPricingDownload(
      abacPermissions,
      user,
      profileData
    ),
    canAccessNavbar: (key) =>
      canAccessNavbarItem(abacPermissions, key, user, profileData),
    canAccessNavbarAction: (itemKey, actionKey) =>
      canAccessNavbarPermission(
        abacPermissions,
        itemKey,
        actionKey,
        user,
        profileData
      ),
    canAccessDefault: (key) =>
      canAccessDefaultSetting(abacPermissions, key, user, profileData),
    canAccessFeatureDocument: (fileName) =>
      canAccessFeatureDocument(abacPermissions, fileName, user, profileData),
    canAccessFeatureDoc: (key) =>
      canAccessPlatformDocFeature(abacPermissions, key, user, profileData),
    canAccessApiDoc: (cardKey) =>
      canAccessPlatformDocApiCard(abacPermissions, cardKey, user, profileData),
    canViewPlatformDocuments: canViewPlatformDocumentsUi(
      abacPermissions,
      user,
      profileData
    ),
    canViewSdkDocuments: canViewSdkDocumentsUi(
      abacPermissions,
      user,
      profileData
    ),
    canViewDashboard: canViewSmsDashboard(
      abacPermissions,
      user,
      profileData
    ),
    canViewSmscProviders: canViewSmscProvidersUi(
      abacPermissions,
      user,
      profileData
    ),
    canViewFirewall: canViewFirewallUi(abacPermissions, user, profileData),
  };
}
